import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { processService } from '../../services/api';
import AnalyzerProcessList from '../analyzer/AnalyzerProcessList';
import AnalyzerSummaryBar from '../analyzer/AnalyzerSummaryBar';
import AnalyzerFilterBar from '../analyzer/AnalyzerFilterBar';
import AnalyzerIssueTable from '../analyzer/AnalyzerIssueTable';
import AnalyzerIssueDetail from '../analyzer/AnalyzerIssueDetail';
import { CATEGORY_LABELS, getCategoryLabel, getIssueCategory } from '../../data/analyzerRules';
import { exportIssuesToCsv } from '../../utils/exportCsv';

const DEFAULT_SEVERITIES = ['Error', 'Warning', 'Info'];
const POLL_INTERVAL_MS = 20000;

const WorkflowAnalyzerTab = () => {
  const { projectId } = useParams();
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processDetails, setProcessDetails] = useState({});
  const [selectedProcessId, setSelectedProcessId] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const [activeSeverities, setActiveSeverities] = useState(DEFAULT_SEVERITIES);
  const [activeCategories, setActiveCategories] = useState([]);
  const [activeFiles, setActiveFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState('none');
  const [sortConfig, setSortConfig] = useState({ key: 'errorSeverity', direction: 'desc' });

  const loadAnalyzerSummary = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const response = await processService.getAnalyzerSummary(projectId);
      setSummary(response.data.data || response.data);
    } catch (err) {
      console.error('Error loading analyzer summary:', err);
      if (!silent) {
        setError('Failed to load Workflow Analyzer data');
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      loadAnalyzerSummary();
    }
  }, [projectId, loadAnalyzerSummary]);

  const loadProcessDetails = async (processId, { force = false } = {}) => {
    if (!force && processDetails[processId]) {
      return;
    }

    setIsDetailLoading(true);
    setDetailError(null);
    try {
      const response = await processService.getProcess(processId);
      const process = response.data;
      const analyzerResult = process.workflowAnalyzerResults?.[0];
      setProcessDetails((prev) => ({
        ...prev,
        [processId]: analyzerResult
      }));
    } catch (err) {
      console.error('Error loading process details:', err);
      setDetailError(err.response?.data?.error || err.message);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const { processes = [], stats = {} } = summary || {};
  const hasRunning = useMemo(
    () => processes.some((process) => process.analyzerStatus === 'running'),
    [processes]
  );

  useEffect(() => {
    if (!selectedProcessId && processes.length > 0) {
      setSelectedProcessId(processes[0].id);
    }
  }, [processes, selectedProcessId]);

  const selectedProcess = processes.find((process) => process.id === selectedProcessId);

  useEffect(() => {
    if (selectedProcessId) {
      loadProcessDetails(selectedProcessId);
      setSelectedIssueId(null);
      resetFilters();
    }
  }, [selectedProcessId]);

  useEffect(() => {
    if (selectedProcessId && selectedProcess?.analyzerStatus === 'completed') {
      loadProcessDetails(selectedProcessId, { force: true });
    }
  }, [selectedProcessId, selectedProcess?.analyzerStatus]);

  useEffect(() => {
    if (!projectId || !hasRunning) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      loadAnalyzerSummary({ silent: true });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [projectId, hasRunning, loadAnalyzerSummary]);

  const formatLastRun = (value) => {
    if (!value) {
      return 'Unknown time';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 'Unknown time';
    }
    return parsed.toLocaleString();
  };

  const issues = useMemo(() => {
    const rawIssues = processDetails[selectedProcessId]?.issues || [];
    return rawIssues.map((issue) => ({
      ...issue,
      category: getIssueCategory(issue),
      categoryLabel: getCategoryLabel(issue.errorCode)
    }));
  }, [processDetails, selectedProcessId]);

  const severityCounts = useMemo(() => (
    issues.reduce((acc, issue) => {
      const key = issue.errorSeverity || 'Info';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, { Error: 0, Warning: 0, Info: 0 })
  ), [issues]);

  const categoryCounts = useMemo(() => (
    issues.reduce((acc, issue) => {
      const key = issue.category || 'OTHER';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {})
  ), [issues]);

  const fileCounts = useMemo(() => (
    issues.reduce((acc, issue) => {
      const key = issue.filePath || 'Unknown file';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {})
  ), [issues]);

  const categoryOptions = useMemo(() => (
    Object.entries(categoryCounts)
      .sort(([, a], [, b]) => b - a)
      .map(([value, count]) => ({ value, label: CATEGORY_LABELS[value] || 'Other', count }))
  ), [categoryCounts]);

  const fileOptions = useMemo(() => (
    Object.entries(fileCounts)
      .sort(([, a], [, b]) => b - a)
      .map(([value, count]) => ({ value, label: value, count }))
  ), [fileCounts]);

  const severityOptions = useMemo(() => (
    Object.entries(severityCounts)
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([value, count]) => ({ value, label: value, count }))
  ), [severityCounts]);

  const resetFilters = () => {
    setActiveSeverities(DEFAULT_SEVERITIES);
    setActiveCategories([]);
    setActiveFiles([]);
    setSearchTerm('');
    setGroupBy('none');
    setSortConfig({ key: 'errorSeverity', direction: 'desc' });
  };

  useEffect(() => {
    if (categoryOptions.length > 0 && activeCategories.length === 0) {
      setActiveCategories(categoryOptions.map((option) => option.value));
    }
  }, [categoryOptions, activeCategories.length]);

  const filteredIssues = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return issues.filter((issue) => {
      if (!activeSeverities.includes(issue.errorSeverity || 'Info')) {
        return false;
      }

      if (activeCategories.length > 0 && !activeCategories.includes(issue.category)) {
        return false;
      }

      if (activeFiles.length > 0 && !activeFiles.includes(issue.filePath || 'Unknown file')) {
        return false;
      }

      if (normalizedSearch) {
        const haystack = [
          issue.errorCode,
          issue.description,
          issue.item,
          issue.recommendation,
          issue.filePath
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(normalizedSearch);
      }

      return true;
    });
  }, [issues, activeSeverities, activeCategories, activeFiles, searchTerm]);

  const selectedIssue = filteredIssues.find((issue) => issue.id === selectedIssueId) || null;
  const selectedIssueIndex = filteredIssues.findIndex((issue) => issue.id === selectedIssueId);

  const activeFilterCount =
    (activeSeverities.length !== DEFAULT_SEVERITIES.length ? 1 : 0) +
    (activeCategories.length !== categoryOptions.length ? 1 : 0) +
    (activeFiles.length > 0 ? 1 : 0) +
    (searchTerm.trim() ? 1 : 0);

  const handleToggleSeverity = (severity) => {
    setActiveSeverities((prev) => {
      if (prev.length === DEFAULT_SEVERITIES.length) {
        return [severity];
      }
      return prev.includes(severity)
        ? prev.filter((value) => value !== severity)
        : [...prev, severity];
    });
  };

  const handleToggleCategory = (category) => {
    setActiveCategories((prev) => {
      if (prev.length === categoryOptions.length) {
        return [category];
      }
      return prev.includes(category)
        ? prev.filter((value) => value !== category)
        : [...prev, category];
    });
  };


  const handleSortChange = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderDetailPanel = () => {
    if (!selectedProcess) {
      return (
        <div className="h-full flex items-center justify-center" style={{color: 'rgba(255,255,255,0.4)'}}>
          Select a process to review analyzer issues.
        </div>
      );
    }

    if (selectedProcess.analyzerStatus === 'failed') {
      return (
        <div className="p-6" style={{border: '1px solid rgba(230,100,97,0.4)', backgroundColor: 'rgba(230,100,97,0.08)', color: 'var(--tqa-red)'}}>
          <div className="text-lg font-semibold mb-2">Analysis failed</div>
          <p className="text-sm">{selectedProcess.analyzerError || 'Unknown error.'}</p>
          <p className="text-xs mt-2" style={{color: 'var(--tqa-red)'}}>
            Last run: {formatLastRun(selectedProcess.analyzerLastRunAt)}
          </p>
        </div>
      );
    }

    if (selectedProcess.analyzerStatus === 'pending') {
      return (
        <div className="p-6" style={{border: '1px solid rgba(245,158,11,0.4)', backgroundColor: 'rgba(245,158,11,0.08)', color: 'rgba(245,158,11,0.9)'}}>
          <div className="text-lg font-semibold mb-2">Analysis pending</div>
          <p className="text-sm">
            Analysis has not started yet. If you just uploaded or synced, this is expected. If it
            stays pending, re-upload the ZIP file or retry analysis.
          </p>
        </div>
      );
    }

    if (selectedProcess.analyzerStatus === 'running') {
      return (
        <div className="p-6" style={{border: '1px solid rgba(130,255,207,0.3)', backgroundColor: 'rgba(130,255,207,0.06)', color: 'var(--tqa-green)'}}>
          <div className="text-lg font-semibold mb-2">Analysis running</div>
          <p className="text-sm">The analyzer is still working.</p>
          <p className="text-xs mt-2" style={{color: 'var(--tqa-green)'}}>
            Last run: {formatLastRun(selectedProcess.analyzerLastRunAt)}
          </p>
        </div>
      );
    }

    if (selectedProcess.analyzerStatus === 'unknown') {
      return (
        <div className="p-6" style={{border: '1px solid var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.65)'}}>
          <div className="text-lg font-semibold mb-2">Status unknown</div>
          <p className="text-sm">
            The analyzer did not report completion in time. You can retry the analysis or re-upload
            the ZIP file.
          </p>
          <p className="text-xs mt-2" style={{color: 'rgba(255,255,255,0.4)'}}>
            Last run: {formatLastRun(selectedProcess.analyzerLastRunAt)}
          </p>
        </div>
      );
    }

    if (isDetailLoading) {
      return (
        <div className="h-full flex items-center justify-center" style={{color: 'rgba(255,255,255,0.4)'}}>
          Loading analyzer issues...
        </div>
      );
    }

    if (detailError) {
      return (
        <div className="p-6" style={{border: '1px solid rgba(230,100,97,0.4)', backgroundColor: 'rgba(230,100,97,0.08)', color: 'var(--tqa-red)'}}>
          <div className="text-lg font-semibold mb-2">Failed to load details</div>
          <p className="text-sm">{detailError}</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <AnalyzerSummaryBar
          severityCounts={severityCounts}
          categoryCounts={categoryCounts}
          totalIssues={issues.length}
          activeSeverities={activeSeverities}
          onToggleSeverity={handleToggleSeverity}
          activeCategories={activeCategories}
          onToggleCategory={handleToggleCategory}
        />

        <AnalyzerFilterBar
          severityOptions={severityOptions}
          activeSeverities={activeSeverities}
          onSelectSeverity={handleToggleSeverity}
          categoryOptions={categoryOptions}
          activeCategories={activeCategories}
          onToggleCategory={handleToggleCategory}
          fileOptions={fileOptions}
          activeFiles={activeFiles}
          onToggleFile={(value) =>
            setActiveFiles((prev) =>
              prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
            )
          }
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          onClearFilters={resetFilters}
          activeFilterCount={activeFilterCount}
          onExport={() => exportIssuesToCsv(filteredIssues, selectedProcess.name)}
        />

        <AnalyzerIssueTable
          issues={filteredIssues}
          groupBy={groupBy}
          selectedIssueId={selectedIssueId}
          onSelectIssue={(issue) => setSelectedIssueId(issue.id)}
          onSortChange={handleSortChange}
          sortConfig={sortConfig}
        />
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{backgroundColor: 'var(--tqa-black)'}}>
      <div className="px-6 py-4" style={{borderBottom: '1px solid var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface)'}}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">Workflow Analyzer</h2>
            <p className="text-xs" style={{color: 'rgba(255,255,255,0.5)'}}>Drill into issue-level detail with filtering and grouping.</p>
          </div>
          <button
            onClick={async () => {
              await loadAnalyzerSummary();
              if (selectedProcessId) {
                await loadProcessDetails(selectedProcessId, { force: true });
              }
            }}
            className="btn-secondary px-3 py-1.5 text-sm font-medium"
          >
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center" style={{color: 'rgba(255,255,255,0.4)'}}>
          Loading Workflow Analyzer data...
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg font-semibold mb-2" style={{color: 'rgba(255,255,255,0.85)'}}>Error loading data</div>
            <p className="mb-4" style={{color: 'rgba(255,255,255,0.5)'}}>{error}</p>
            <button onClick={loadAnalyzerSummary} className="btn-primary">
              Try again
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          <AnalyzerProcessList
            processes={processes}
            stats={stats}
            selectedProcessId={selectedProcessId}
            onSelectProcess={(process) => setSelectedProcessId(process.id)}
          />
          <div className="flex-1 overflow-y-auto p-6" style={{backgroundColor: 'var(--tqa-black)'}}>
            {renderDetailPanel()}
          </div>
        </div>
      )}

      <AnalyzerIssueDetail
        issue={selectedIssue}
        onClose={() => setSelectedIssueId(null)}
        onPrev={() => setSelectedIssueId(filteredIssues[selectedIssueIndex + 1]?.id || null)}
        onNext={() => setSelectedIssueId(filteredIssues[selectedIssueIndex - 1]?.id || null)}
        hasPrev={selectedIssueIndex >= 0 && selectedIssueIndex < filteredIssues.length - 1}
        hasNext={selectedIssueIndex > 0}
      />
    </div>
  );
};

export default WorkflowAnalyzerTab;
